import { useState, useEffect, useCallback } from 'react';

import { apiService } from '@/lib/axios';

export function useDocuments({ sortBy = null, sortDirection = 'asc' } = {}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.documents.getAll({
        sortBy,
        sortDirection,
        limit: 100,
      });

      if (response.data.success) {
        const transformed = response.data.data.map((document) => ({
          id: document.id,
          revision: document.revision,
          status: document.status,
          companyLogo: document.companyLogo,
          companyName: document.companyName,
          creator: document.creator,
          creatorName: document.creatorName,
          documentNumber: document.documentNumber,
          owner: document.owner,
          releasedAt: document.releasedAt,
          serial: document.serial,
          documentTitle: document.title,
          templateId: document.templateId,
          reviewerPipeline: document.reviewerPipeline,
          currentActorIndex: document.currentActorIndex,
        }));
        setDocuments(() => transformed);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDirection]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  return { documents, loading, error, refetch: loadDocuments };
}

const normalizedVariantFieldData = (originalList, fieldType) =>
  (Array.isArray(originalList) ? originalList : []).reduce(
    (acc, appendixData, index) => {
      const itemId = `${+new Date()}${index}`;

      acc.formData[itemId] = {
        title: appendixData.title,
        value:
          fieldType !== 'multi-text'
            ? appendixData.value
            : appendixData.value.map((fieldData, j) => ({
                id: `${+new Date()}${j}`,
                order: j,
                value: fieldData,
              })),
      };
      acc.rows.push({
        id: itemId,
        type: fieldType
          ? fieldType
          : Array.isArray(appendixData.value)
            ? 'table'
            : /data:image/.test(appendixData.value)
              ? 'file'
              : 'textarea',
      });

      return acc;
    },
    { formData: {}, rows: [] }
  );

export const normalizeDocument = (apiData) => ({
  status: apiData.status,
  revision: apiData.revision,
  reviewers: apiData.reviewers,
  reviewerPipeline: apiData.reviewerPipeline,
  currentActorIndex: apiData.currentActorIndex,
  isRejected: apiData.isRejected,
  rejectedBy: apiData.rejectedBy,
  rejectedReason: apiData.rejectedReason,
  approvedAt: apiData.approvedAt,
  releasedAt: apiData.releasedAt,
  nextPeriodReviewDate: apiData.nextPeriodReviewDate,
  archivedAt: apiData.archivedAt,
  appendix: normalizedVariantFieldData(apiData.appendix),
  clause: {
    clauses: (Array.isArray(apiData.clause) ? apiData.clause : []).map(
      (clauseData, index) => ({
        expanded: true,
        id: `${+new Date()}${index}`,
        title: clauseData.title,
        ...normalizedVariantFieldData(clauseData.value),
      })
    ),
  },
  creator: apiData.creator,
  serial: apiData.serial,
  documentNumber: apiData.documentNumber,
  id: apiData.id,
  templateId: apiData.templateId,
  documentTitle: apiData.title,
  companyLogo: apiData.companyLogo,
  companyName: apiData.companyName,
  division: apiData.division,
  owner: apiData.owner,
  objective: apiData.objective,
  references: (Array.isArray(apiData.references) ? apiData.references : []).map(
    (referenceData, index) => ({
      id: `${+new Date()}${index}`,
      order: index,
      value: { number: referenceData.serial, title: referenceData.title },
    })
  ),
  responsibilities: {
    ...(apiData.responsibilities &&
      normalizedVariantFieldData(apiData.responsibilities, 'multi-text')),
  },
  scope: apiData.scope,
  issueDate: apiData.issueDate,
  effectiveDate: apiData.effectiveDate,
  terms: (Array.isArray(apiData.terms) ? apiData.terms : []).map(
    (termData, index) => ({
      id: `${+new Date()}${index}`,
      order: index,
      value: termData,
    })
  ),
});

export function useDocumentRevisions(id) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.documents.getRevisions(id);
      if (response.data.success) {
        setRevisions(response.data.data);
      }
    } catch (err) {
      setError('Failed to load revisions');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { revisions, loading, error, refetch: load };
}

export const useDocumentById = () => {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setDocument(null);
    setError(null);
  }, []);

  const load = useCallback(async (documentId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.documents.getById(documentId);

      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to load document');
      }

      const normalized = normalizeDocument(response.data.data);

      setDocument(normalized);

      return normalized;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to load document';

      setError(message);

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { document, loading, error, load, reset };
};
